import { useAtom, useAtomValue } from "jotai";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
	audioErrorAtom,
	audioTaskStateAtom,
} from "$/modules/audio/states/index.ts";
import { type ToastId, toast } from "$/utils/toast";

export const useAudioFeedback = () => {
	const taskState = useAtomValue(audioTaskStateAtom);
	const [errorMsg, setErrorMsg] = useAtom(audioErrorAtom);
	const toastId = useRef<ToastId | null>(null);
	const { t } = useTranslation();

	useEffect(() => {
		const getMessage = (type: string) => {
			switch (type) {
				case "TRANSCODING":
					return t(
						"audio.status.transcoding",
						"ブラウザが対応していない形式のため、音声を変換しています…（大きなファイルでは時間がかかることがあります）",
					);
				case "LOADING":
					return t("audio.status.loading", "音声を読み込み中…");
				default:
					return t("audio.status.processing", "処理中…");
			}
		};

		if (taskState) {
			const { type } = taskState;
			const message = getMessage(type);

			if (toastId.current === null) {
				toastId.current = toast.loading(message);
			} else {
				toast.update(toastId.current, {
					render: message,
				});
			}
		} else {
			if (toastId.current !== null) {
				// Skip obsolete queued progress messages. Visible notifications keep
				// the standard duration until a successor arrives.
				toast.cancelPending(toastId.current);
				toastId.current = null;
			}
		}
	}, [taskState, t]);

	useEffect(() => {
		if (errorMsg) {
			toast.error(
				`${t("audio.error.workerError", "音声の処理中にエラーが発生しました")}: ${errorMsg}`,
			);

			setErrorMsg(null);
		}
	}, [errorMsg, setErrorMsg, t]);
};
